import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { DashboardLayout } from '@/config/admin-dashboard';

/**
 * Persistence for the customizable admin dashboard.
 *
 * Two layers (resolution precedence is applied in DashboardHome):
 *  - per-admin: localStorage (`admin-dashboard-layout`), like the sidebar's
 *    `admin-nav-open-sections`.
 *  - per-site: `site_settings.dashboard_layout` via `/admin/dashboard/layout`
 *    (the "Save as site default" that applies to every admin of the tenant).
 */

const LOCAL_KEY = 'admin-dashboard-layout';

function isLayout(v: unknown): v is DashboardLayout {
  return (
    !!v &&
    typeof v === 'object' &&
    Array.isArray((v as DashboardLayout).order) &&
    Array.isArray((v as DashboardLayout).hidden)
  );
}

export function readLocalLayout(): DashboardLayout | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isLayout(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeLocalLayout(layout: DashboardLayout): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(layout));
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function clearLocalLayout(): void {
  try {
    localStorage.removeItem(LOCAL_KEY);
  } catch {
    /* ignore */
  }
}

/** Per-site saved layout (the tenant default) + a mutation to save it. */
export function useSiteDashboardLayout() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['dashboard', 'layout', 'site'],
    queryFn: async () =>
      (await api.get<{ layout: DashboardLayout | null }>('/admin/dashboard/layout')).layout,
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (layout: DashboardLayout) =>
      api.put<{ layout: DashboardLayout }>('/admin/dashboard/layout', { layout }),
    onSuccess: (_res, layout) => {
      qc.setQueryData(['dashboard', 'layout', 'site'], layout);
    },
  });

  return { siteLayout: query.data ?? null, isLoading: query.isLoading, save };
}
